// src/features/auth/DeviceChangeVerification.jsx

import React, { useMemo, useState } from "react";
import {
  ShieldCheck,
  Mail,
  MessageCircle,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";

import { functions } from "../../firebase";
import { TENANT_ID } from "../../tenant";
import { getDeviceLabel } from "../../lib/deviceIdentity";

const DeviceChangeVerification = ({
  deviceId,
  appConfig,
  onVerified,
  onStartFresh,
  initialMaskedEmail = "",
  reason = "",
}) => {
  const [step, setStep] = useState("intro");
  const [requestId, setRequestId] = useState("");
  const [maskedEmail, setMaskedEmail] = useState(initialMaskedEmail);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  const supportUrl = useMemo(() => {
    return (
      appConfig?.support_line_url ||
      appConfig?.support_url ||
      appConfig?.line_url ||
      ""
    );
  }, [appConfig]);

  const handleStartEmailVerification = async () => {
    setError("");
    setSending(true);

    try {
      const callable = httpsCallable(functions, "startDeviceChangeVerification");

      const res = await callable({
        tenantId: TENANT_ID,
        deviceId,
        deviceLabel: getDeviceLabel(),
      });

      const data = res.data || {};

      if (!data.ok || !data.requestId) {
        throw new Error("確認コードの送信に失敗しました。");
      }

      setRequestId(data.requestId);
      setMaskedEmail(data.maskedEmail || "");
      setCode("");
      setStep("code");
    } catch (err) {
      console.error("[DeviceChangeVerification] start failed", err);

      if (err?.code === "functions/failed-precondition") {
        setError(
          "登録済みメールアドレスが見つかりません。サポートへご連絡ください。"
        );
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError(
          "確認コードを送信できませんでした。時間をおいて再度お試しください。"
        );
      }
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async () => {
    const normalizedCode = String(code || "").replace(/\D/g, "");

    if (normalizedCode.length !== 6) {
      setError("6桁の確認コードを入力してください。");
      return;
    }

    setError("");
    setVerifying(true);

    try {
      const callable = httpsCallable(functions, "verifyDeviceChangeCode");

      const res = await callable({
        tenantId: TENANT_ID,
        requestId,
        code: normalizedCode,
        deviceId,
        deviceLabel: getDeviceLabel(),
      });

      const data = res.data || {};

      if (!data.ok) {
        throw new Error("確認に失敗しました。");
      }

      onVerified?.();
    } catch (err) {
      console.error("[DeviceChangeVerification] verify failed", err);

      if (err?.code === "functions/deadline-exceeded") {
        setError(
          "確認コードの有効期限が切れています。もう一度メールを送信してください。"
        );
      } else if (err?.code === "functions/resource-exhausted") {
        setError(
          "確認回数の上限を超えました。もう一度メールを送信してください。"
        );
      } else if (err?.code === "functions/permission-denied") {
        setError("確認コードが正しくありません。");
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError("確認できませんでした。コードをご確認ください。");
      }
    } finally {
      setVerifying(false);
    }
  };

    const handleSupport = () => {
        if (supportUrl) {
        window.location.href = supportUrl;
        return;
        }

        const supportEmail = String(appConfig?.support_email || "").trim();

        if (supportEmail) {
        const subject = encodeURIComponent("端末変更の確認について");
        const body = encodeURIComponent(
            "端末変更の確認について相談したいです。\n\n登録電話番号：\nお名前：\n"
        );

        window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
        return;
        }

        setError(
        "サポート窓口のURLがまだ設定されていません。店頭または施設スタッフへお問い合わせください。"
        );
    };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
          {step === "intro" && (
            <>
              <div className="w-14 h-14 rounded-3xl bg-gray-50 flex items-center justify-center mb-5">
                <ShieldCheck className="text-gray-900" size={28} />
              </div>

              <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                端末変更の確認
              </h1>

              <p className="mt-3 text-sm leading-7 text-gray-600 font-medium">
                新しい端末でご利用いただくため、確認が必要です。
              </p>

              <p className="mt-2 text-sm leading-7 text-gray-500">
                登録済みメールアドレスに確認コードをお送りします。
              </p>

              {reason === "protected_account_without_email" && (
                <div className="mt-5 rounded-3xl bg-amber-50 border border-amber-100 p-4">
                  <p className="text-sm text-amber-800 font-bold leading-6">
                    登録済みメールアドレスがないため、サポートでの確認が必要です。
                  </p>
                </div>
              )}

              {error && (
                <div className="mt-5 rounded-3xl bg-red-50 border border-red-100 p-4 flex gap-3">
                  <AlertCircle
                    className="text-red-500 shrink-0 mt-0.5"
                    size={18}
                  />
                  <p className="text-sm text-red-700 font-bold leading-6">
                    {error}
                  </p>
                </div>
              )}

              <div className="mt-7 space-y-3">
                <button
                  onClick={handleStartEmailVerification}
                  disabled={sending || reason === "protected_account_without_email"}
                  className="w-full rounded-3xl bg-gray-900 text-white font-black py-4 flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
                >
                  {sending ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Mail size={18} />
                  )}
                  メールで確認する
                </button>

                <button
                  onClick={handleSupport}
                  className="w-full rounded-3xl bg-white border border-gray-200 text-gray-700 font-black py-4 flex items-center justify-center gap-2"
                >
                  <MessageCircle size={18} />
                  サポートへ連絡する
                </button>
              </div>

              {onStartFresh && (
                <button
                  onClick={onStartFresh}
                  className="mt-5 w-full text-center text-xs font-bold text-gray-500"
                >
                  はじめから登録する
                </button>
              )}
            </>
          )}

          {step === "code" && (
            <>
              <button
                onClick={() => {
                  setStep("intro");
                  setError("");
                }}
                className="mb-5 inline-flex items-center gap-1 text-sm font-bold text-gray-500"
              >
                <ArrowLeft size={16} />
                戻る
              </button>

              <div className="w-14 h-14 rounded-3xl bg-gray-50 flex items-center justify-center mb-5">
                <Mail className="text-gray-900" size={28} />
              </div>

              <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                確認コードを入力
              </h1>

              <p className="mt-3 text-sm leading-7 text-gray-600 font-medium">
                {maskedEmail || "登録済みメールアドレス"} に確認コードをお送りしました。
              </p>

              <input
                value={code}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCode(next);
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6桁のコード"
                className="mt-6 w-full rounded-xl border border-gray-200 bg-white px-5 py-4 text-center text-2xl font-black tracking-[0.35em] outline-none hover:border-gray-300 focus:border-gray-900 focus:bg-white"
              />

              {error && (
                <div className="mt-5 rounded-3xl bg-red-50 border border-red-100 p-4 flex gap-3">
                  <AlertCircle
                    className="text-red-500 shrink-0 mt-0.5"
                    size={18}
                  />
                  <p className="text-sm text-red-700 font-bold leading-6">
                    {error}
                  </p>
                </div>
              )}

              <button
                onClick={handleVerifyCode}
                disabled={verifying || code.length !== 6}
                className="mt-7 w-full rounded-3xl bg-gray-900 text-white font-black py-4 flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
              >
                {verifying ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={18} />
                )}
                確認して進む
              </button>

              <button
                onClick={handleStartEmailVerification}
                disabled={sending}
                className="mt-4 w-full text-center text-sm font-bold text-gray-500 disabled:opacity-50"
              >
                コードを再送する
              </button>
            </>
          )}
        </div>

        <p className="mt-5 text-center text-xs leading-6 text-gray-500">
          心あたりがない場合は、確認を進めずサポートへご連絡ください。
        </p>
      </div>
    </div>
  );
};

export default DeviceChangeVerification;